import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authAPI, referralAPI } from "../services/api";
import TermsModal from "../components/TermsModal";
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n, nextLang } from '../i18n';
import Seo from '../components/Seo';
import { track, trackNow } from '../utils/track';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

const TEAM_SUFFIXES = ['FC', 'United', 'City', 'Stars', 'Legends', 'Academy', 'Warriors', 'Wolves', 'Eagles', 'Phoenix', 'Thunder', 'Force'];

const Register = () => {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [teamName, setTeamName] = useState("");
  const [coachName, setCoachName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [referralCoach, setReferralCoach] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const refFromUrl = searchParams.get('ref');
    if (refFromUrl) setReferralCode(refFromUrl.toUpperCase());
  }, [searchParams]);

  const suggestTeamName = (name) => {
    if (!name || teamName) return;
    const suffix = TEAM_SUFFIXES[Math.floor(Math.random() * TEAM_SUFFIXES.length)];
    const base = name.split(' ')[0];
    setTeamName(`${base} ${suffix}`);
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setIsGoogleLoading(true);
    track('register_start', { method: 'google' });
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await firebase.auth().signInWithPopup(provider);
      const idToken = await result.user.getIdToken();
      const res = await authAPI.googleAuth(idToken, teamName || null);
      trackNow((res?.isNewUser ?? res?.data?.isNewUser) ? 'signup_completed' : 'login_completed', { method: 'google', from: 'register' });
      navigate("/game");
    } catch (error) {
      console.error("Google sign-in error:", error);
      if (error.code === 'auth/popup-closed-by-user') { track('register_abandoned', { method: 'google', reason: 'popup_closed' }); setIsGoogleLoading(false); return; }
      const errorMsg = error.response?.data?.message || error.message || "Google sign-in failed";
      track('register_error', { method: 'google', reason: String(errorMsg).slice(0, 120) });
      setError(errorMsg);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleRegister = async () => {
    setError("");
    // Client-side validation failures are tracked too: they show which field stops people.
    const invalid = (field, msg) => { track('register_error', { method: 'email', reason: `invalid_${field}` }); setError(msg); };
    if (!email) return invalid('email', t('reg.emailIsRequired'));
    if (!coachName) return invalid('coach_name', t('reg.coachNameIsRequired'));
    if (!teamName) return invalid('team_name', t('reg.teamNameIsRequired'));
    if (!/^\S+@\S+\.\S+$/.test(email)) return invalid('email_format', t('reg.invalidEmail'));
    if (!password || password.length < 6) return invalid('password', t('reg.passwordMustBeAt'));
    track('register_submit', { method: 'email', referral: !!referralCode });

    setIsLoading(true);
    try {
      await authAPI.register({
        email,
        password,
        coachName,
        teamName,
        ...(referralCode && { referralCode: referralCode.trim().toUpperCase() }),
      });
      trackNow('signup_completed', { method: 'email', referral: !!referralCode });
      navigate("/game");
    } catch (error) {
      const errorMsg = error.response?.data?.errors?.[0]?.msg ||
                       error.response?.data?.message ||
                       (t('reg.registrationFailedPleaseTry'));
      track('register_error', { method: 'email', reason: String(errorMsg).slice(0, 120), status: error.response?.status || 0 });
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const features = t('reg.features').split('|');

  return (
    <motion.div
      className="relative min-h-screen w-full flex justify-center items-start md:items-center overflow-hidden px-4 py-6 md:py-8"
      style={{ background: 'var(--surface-0)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Seo
        title="Create Your Free Football Club | My Football Game"
        description="Create your football club in 30 seconds and play your first match straight away. Free online football manager: squad, tactics, seasons. No download."
        path="/register"
      />
      <div className="absolute top-4 right-4 z-50">
        <button
          onClick={() => setLang(nextLang(lang))}
          className="px-3 py-1.5 rounded text-[12px] font-bold tracking-wider border transition-all"
          style={{ background: 'var(--surface-1)', color: 'var(--text-2)', borderColor: 'var(--border-edge)' }}
        >
          {nextLang(lang).toUpperCase()}
        </button>
      </div>

      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <linearGradient id="pcVignette" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(10,15,14,0)" />
            <stop offset="100%" stopColor="rgba(10,15,14,0.95)" />
          </linearGradient>
        </defs>
        <g fill="none" stroke="rgba(184,255,61,0.08)" strokeWidth="0.75">
          <rect x="120" y="180" width="1200" height="540" />
          <line x1="720" y1="180" x2="720" y2="720" />
          <circle cx="720" cy="450" r="80" />
        </g>
        <rect x="0" y="0" width="1440" height="900" fill="url(#pcVignette)" />
      </svg>

      <div className="relative z-10 w-full max-w-md">
        {/* Compact header */}
        <div className="text-center mb-5">
          <a href="/" className="inline-block mb-2">
            <img alt="My Football Game" className="w-12 h-12 object-contain mx-auto" src="/logo.png" />
          </a>
          <h1 className="pc-display text-4xl md:text-5xl text-[var(--text-1)]">
            {t('reg.play')}<span style={{ color: 'var(--accent)' }}>.</span>{t('reg.now')}
          </h1>
          <p className="text-[13px] text-[var(--text-3)] mt-1">{t('reg.freeNoDownload30')}</p>
        </div>

        {/* Main card */}
        <div
          className="relative rounded-xl p-5 md:p-6"
          style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)', boxShadow: '0 32px 64px -16px rgba(0,0,0,0.6)' }}
        >
          {/* Social proof */}
          <div className="flex items-center justify-center gap-2 mb-4 py-2 rounded-lg" style={{ background: 'rgba(184,255,61,0.06)' }}>
            <div className="flex -space-x-2">
              {['#4285F4', '#EA4335', '#FBBC05', '#34A853'].map((c, i) => (
                <div key={i} className="w-6 h-6 rounded-full border-2 flex items-center justify-center text-[9px] font-bold text-white" style={{ background: c, borderColor: 'var(--surface-1)' }}>
                  {['P','M','J','A'][i]}
                </div>
              ))}
            </div>
            <span className="text-[12px] font-medium" style={{ color: 'var(--text-2)' }}>
              {t('reg.100CoachesPlaying')}
            </span>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                className="mb-3 px-3 py-2 rounded text-[12px]"
                style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.30)', color: 'var(--loss)' }}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Google Sign-In — PRIMARY CTA */}
          <motion.button
            className="w-full py-4 px-5 rounded-lg text-[15px] font-bold tracking-wide transition-all flex items-center justify-center gap-3 mb-3"
            style={{ background: '#ffffff', color: '#1f1f1f', fontFamily: 'var(--font-body)', border: '1px solid #dadce0' }}
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
            whileTap={{ scale: 0.98 }}
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {isGoogleLoading ? (t('reg.signingIn')) : (t('reg.signUpWithGoogle'))}
          </motion.button>

          <p className="text-center text-[11px] mb-4" style={{ color: 'var(--text-3)' }}>
            {t('reg.1ClickTeamAuto')}
          </p>

          {/* Email toggle */}
          {!showEmailForm ? (
            <div
              onClick={() => { setShowEmailForm(true); track('register_start', { method: 'email' }); }}
              role="button"
              tabIndex={0}
              className="w-full py-2.5 text-[12px] uppercase tracking-widest text-center transition-colors cursor-pointer select-none"
              style={{ color: 'var(--text-3)' }}
            >
              {t('reg.orSignUpWith')}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px" style={{ background: 'var(--border-edge)' }} />
                <span className="text-[11px] uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>
                  {t('reg.email2')}
                </span>
                <div className="flex-1 h-px" style={{ background: 'var(--border-edge)' }} />
              </div>

              {renderInput('Email', "email", email, setEmail, null, 'coach@example.com')}
              {renderInput(t('reg.password2'), "password", password, setPassword, null, t('reg.min6Characters'))}
              {renderInput(t('reg.coachName'), "text", coachName, setCoachName, (val) => suggestTeamName(val), t('reg.eGAlexFerguson'))}
              {renderInput(t('reg.teamName2'), "text", teamName, setTeamName, null, t('reg.eGThunderFc'))}

              {/* Referral code */}
              <details className="mb-3">
                <summary className="text-[11px] uppercase tracking-wider cursor-pointer select-none" style={{ color: 'var(--text-3)' }}>
                  {t('reg.inviteCodeOptional')}
                </summary>
                <div className="mt-2">
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 12);
                      setReferralCode(v);
                      setReferralValid(null);
                      setReferralCoach("");
                      if (v.length >= 4) {
                        referralAPI.validateCode(v).then(r => {
                          if (r.data?.valid) { setReferralValid(true); setReferralCoach(r.data.coach_name); }
                          else setReferralValid(false);
                        }).catch(() => setReferralValid(false));
                      }
                    }}
                    placeholder="e.g. PAUL3X7F"
                    className="w-full py-2.5 px-3 rounded text-[14px] outline-none transition-all uppercase"
                    style={{
                      background: 'var(--surface-2)',
                      border: `1px solid ${referralValid === true ? 'var(--win)' : referralValid === false ? 'var(--loss)' : 'var(--border-edge)'}`,
                      color: 'var(--text-1)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  />
                  {referralValid === true && (
                    <div className="mt-1 text-[11px] font-medium" style={{ color: 'var(--win)' }}>
                      {t('reg.invitedBy', { coach: referralCoach })}
                    </div>
                  )}
                  {referralValid === false && referralCode.length >= 4 && (
                    <div className="mt-1 text-[11px] font-medium" style={{ color: 'var(--loss)' }}>
                      {t('reg.invalidCode')}
                    </div>
                  )}
                </div>
              </details>

              <motion.button
                className="w-full py-3.5 px-5 rounded-lg text-[13px] font-bold uppercase tracking-[0.15em] transition-all disabled:opacity-50"
                style={{ background: 'var(--accent)', color: 'var(--surface-0)', fontFamily: 'var(--font-body)', boxShadow: '0 0 24px var(--accent-glow)' }}
                onClick={handleRegister}
                disabled={isLoading}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? (t('reg.creating2')) : (t('reg.createTeam'))}
              </motion.button>
              <p className="text-center text-[10px] mt-2" style={{ color: 'var(--text-3)' }}>
                {t('reg.bySigningUpYou')}
                <span className="underline cursor-pointer" style={{ color: 'var(--accent)' }} onClick={() => setShowTermsModal(true)}>
                  {t("reg.terms")}
                </span>
              </p>
            </div>
          )}

          {/* Feature pills */}
          <div className="flex flex-wrap gap-1.5 justify-center mt-4 pt-4" style={{ borderTop: '1px solid var(--border-edge)' }}>
            {features.map((f, i) => (
              <span key={i} className="text-[10px] px-2.5 py-1 rounded-full font-medium" style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                {f}
              </span>
            ))}
          </div>

          <div className="mt-4 text-center text-[12px]" style={{ color: 'var(--text-3)' }}>
            {t("reg.hasAccount")} <span className="cursor-pointer transition-colors" style={{ color: 'var(--accent)', fontWeight: 600 }} onClick={() => navigate('/login')}>
              {t("reg.login")}
            </span>
          </div>
        </div>
      </div>
      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </motion.div>
  );

  function renderInput(label, type, value, setter, onBlurExtra, placeholder) {
    return (
      <div className="relative mb-2.5">
        <div className="pc-eyebrow mb-1">{label}</div>
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => setter(e.target.value)}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-edge)';
            e.currentTarget.style.boxShadow = 'none';
            if (onBlurExtra) onBlurExtra(e.target.value);
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleRegister(); }}
          className="w-full py-2.5 px-3 rounded text-[14px] outline-none transition-all"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border-edge)', color: 'var(--text-1)', fontFamily: 'var(--font-mono)' }}
          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 1px var(--accent-dim)'; }}
        />
      </div>
    );
  }
};

export default Register;
